import React from "react";
import type { GuestProfile } from "../../types/guest"; // Updated import path
import SimpleDate from "../common/SimpleDate";
import { formatDateOnlyForDisplay } from "../../utils/dateFormatting";
import { useNavigate } from "react-router-dom";

// Helper function to format status text by adding spaces before capital letters
const formatStatus = (status: string): string => {
  return status.replace(/([a-z])([A-Z])/g, "$1 $2");
};

interface ExpandedGuestProfileCardProps {
  guest: GuestProfile;
}

// Helper component for consistent section styling
const ProfileSection: React.FC<{
  title: string;
  children: React.ReactNode;
  constrainContent?: boolean; // Optional prop to make the content area scrollable and constrained
}> = ({ title, children, constrainContent = false }) => (
  <div className="p-5 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6 h-full flex flex-col">
    <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90 mb-4 flex-shrink-0">
      {title}
    </h4>
    <div className="border-t border-gray-200 dark:border-gray-700 -mx-5 lg:-mx-6 mb-4 flex-shrink-0"></div>
    <div className={constrainContent ? "flex-1 min-h-0" : ""}>{children}</div>
  </div>
);

const ExpandedGuestProfileCard: React.FC<ExpandedGuestProfileCardProps> = ({
  guest,
}) => {
  const navigate = useNavigate();

  if (!guest) return null;

  // Navigate to specific request page with deep link
  const handleRequestClick = (requestType: string, requestId: string) => {
    let targetPath = "";

    // Map request types to their corresponding pages
    switch (requestType.toLowerCase()) {
      case "general":
        targetPath = "/general";
        break;
      case "dining":
      case "in-room dining":
        targetPath = "/in-room-dining";
        break;
      case "reservation":
        targetPath = "/reservations";
        break;
      default:
        // Default to general requests for unknown types
        targetPath = "/general";
    }

    // Navigate with requestId parameter for deep linking
    navigate(`${targetPath}?requestId=${requestId}`);
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
      {/* Meta Section - like UserMetaCard */}
      <div className="p-5 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6 mb-6">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-col items-center w-full gap-6 xl:flex-row">
            <div className="w-20 h-20 overflow-hidden border border-gray-200 rounded-full dark:border-gray-800 bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-3xl text-gray-500 dark:text-gray-400">
              {guest.name.substring(0, 1)}
            </div>
            <div className="order-3 xl:order-2 text-center xl:text-left">
              <h3 className="mb-1 text-xl font-semibold text-gray-800 dark:text-white/90">
                {guest.title ? `${guest.title} ${guest.name}` : guest.name}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Guest ID: {guest.guestId}
              </p>
              <p
                className={`text-sm font-medium mt-1 ${
                  guest.status === "CheckedIn"
                    ? "text-green-600 dark:text-green-400"
                    : guest.status === "CheckedOut"
                    ? "text-gray-500 dark:text-gray-400"
                    : "text-blue-600 dark:text-blue-400"
                }`}
              >
                Status: {formatStatus(guest.status)}
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-500 dark:text-gray-400">
                {guest.gender && <span>{guest.gender}</span>}
                {guest.dateOfBirth && (
                  <span>
                    DOB: {formatDateOnlyForDisplay(guest.dateOfBirth)}
                  </span>
                )}
              </div>
            </div>
          </div>
          {/* Action buttons for the meta card could go here if needed */}
        </div>
      </div>

      {/* Main content grid - Now a single grid for all sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {/* All ProfileSection components will be direct children here */}
        <ProfileSection title="Stay Information">
          <div className="space-y-2 text-sm">
            <p>
              <span className="font-medium text-gray-600 dark:text-gray-400">
                Check-in:
              </span>{" "}
              {guest.checkInDate ? (
                <SimpleDate date={guest.checkInDate} />
              ) : (
                "N/A"
              )}
            </p>
            <p>
              <span className="font-medium text-gray-600 dark:text-gray-400">
                Check-out:
              </span>{" "}
              {guest.checkOutDate ? (
                <SimpleDate date={guest.checkOutDate} />
              ) : (
                "N/A"
              )}
            </p>
            <p>
              <span className="font-medium text-gray-600 dark:text-gray-400">
                Email:
              </span>{" "}
              {guest.email || "N/A"}
            </p>
            <p>
              <span className="font-medium text-gray-600 dark:text-gray-400">
                Phone:
              </span>{" "}
              {guest.phone || "N/A"}
            </p>
          </div>
        </ProfileSection>

        <ProfileSection title="Room Information">
          <div className="space-y-2 text-sm">
            <p>
              <span className="font-medium text-gray-600 dark:text-gray-400">
                Room No.:
              </span>{" "}
              {guest.room.roomNumber}
            </p>
            <p>
              <span className="font-medium text-gray-600 dark:text-gray-400">
                Type:
              </span>{" "}
              {guest.room.type}
            </p>
            <p>
              <span className="font-medium text-gray-600 dark:text-gray-400">
                Floor:
              </span>{" "}
              {guest.room.floor}
            </p>
            <p>
              <span className="font-medium text-gray-600 dark:text-gray-400">
                Room ID:
              </span>{" "}
              {guest.room.roomId}
            </p>
          </div>
        </ProfileSection>

        {guest.otherGuests.length > 0 && (
          <ProfileSection title="Accompanying Guests">
            <ul className="space-y-2">
              {guest.otherGuests.map((og) => (
                <li
                  key={og.guestId}
                  className="p-2 border border-gray-200 dark:border-gray-700 rounded-md text-sm"
                >
                  <p className="font-medium text-gray-800 dark:text-gray-100">
                    {og.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    ID: {og.guestId}
                  </p>
                </li>
              ))}
            </ul>
          </ProfileSection>
        )}

        <ProfileSection title="Billing Summary">
          {guest.recentCharges.length > 0 ? (
            <div className="max-h-48 overflow-y-auto text-sm space-y-2 mb-3">
              {guest.recentCharges.map((charge) => (
                <div key={charge.chargeId} className="flex justify-between">
                  <span className="text-gray-700 dark:text-gray-200">
                    {charge.description}
                  </span>
                  <span className="text-gray-600 dark:text-gray-300">
                    ${charge.amount.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No recent charges.
            </p>
          )}
          <div className="border-t border-gray-200 dark:border-gray-600 -mx-5 lg:-mx-6 my-3"></div>
          <div className="flex justify-between items-center">
            <p className="text-md font-semibold text-gray-800 dark:text-gray-100">
              Total Bill:
            </p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">
              ${guest.totalBill.toFixed(2)}
            </p>
          </div>
        </ProfileSection>

        <ProfileSection title="Preferences">
          {/* Dietary Restrictions */}
          {guest.preferencesDietaryRestrictions &&
            guest.preferencesDietaryRestrictions.length > 0 && (
              <div className="mb-4">
                <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Dietary Restrictions
                </h5>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                  {guest.preferencesDietaryRestrictions.map((pref) => (
                    <li key={`diet-${pref.dietaryRestrictionId}`}>
                      {pref.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}

          {/* Accessibility Needs */}
          {guest.preferencesAccessibilityNeeds &&
            guest.preferencesAccessibilityNeeds.length > 0 && (
              <div className="mb-4">
                <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Accessibility Needs
                </h5>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                  {guest.preferencesAccessibilityNeeds.map((pref, idx) => (
                    <li key={`access-${idx}`}>
                      {pref
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </li>
                  ))}
                </ul>
              </div>
            )}

          {/* Communication Preferences */}
          {guest.preferencesCommunication &&
            guest.preferencesCommunication.length > 0 && (
              <div className="mb-4">
                <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Communication Preferences
                </h5>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                  {guest.preferencesCommunication.map((pref, idx) => (
                    <li key={`comm-${idx}`}>
                      {pref
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </li>
                  ))}
                </ul>
              </div>
            )}

          {/* General Stay and Interests */}
          {guest.preferencesGeneral && guest.preferencesGeneral.length > 0 && (
            <div className="mb-4">
              <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                General Stay and Interests
              </h5>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                {guest.preferencesGeneral.map((pref) => (
                  <li key={`gen-${pref}`}>{pref}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Hotel-specific Interests */}
          {guest.preferencesHotelInterests &&
            guest.preferencesHotelInterests.length > 0 && (
              <div className="mb-4">
                <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Hotel Interests
                </h5>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                  {guest.preferencesHotelInterests.map((pref) => (
                    <li key={`hotel-${pref}`}>{pref}</li>
                  ))}
                </ul>
              </div>
            )}
        </ProfileSection>

        <ProfileSection title="Request History" constrainContent>
          {guest.requestHistory.length > 0 ? (
            <ul className="space-y-2 text-sm overflow-y-auto h-full">
              {guest.requestHistory.map((req) => (
                <li
                  key={req.requestId}
                  className="p-2 border border-gray-100 dark:border-gray-700/50 rounded-md flex justify-between items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  onClick={() =>
                    handleRequestClick(req.requestType, req.requestId)
                  }
                >
                  <div>
                    <p className="font-medium text-gray-800 dark:text-gray-100">
                      {req.requestType}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      ID: {req.requestId} -{" "}
                      {req.submittedAt ? (
                        <SimpleDate date={req.submittedAt} />
                      ) : (
                        "N/A"
                      )}
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      Click to view details
                    </p>
                  </div>
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      req.status === "Completed"
                        ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                        : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300"
                    }`}
                  >
                    {formatStatus(req.status)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No request history.
            </p>
          )}
        </ProfileSection>

        <ProfileSection title="Guest Analytics">
          <div className="p-6 text-center">
            <div className="text-gray-400 dark:text-gray-500 mb-2">
              <svg
                className="w-12 h-12 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <h4 className="text-lg font-medium text-gray-600 dark:text-gray-400 mb-2">
              Coming Soon
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              Guest analytics and insights will be available in a future update.
            </p>
          </div>
        </ProfileSection>
      </div>
    </div>
  );
};

export default ExpandedGuestProfileCard;

// Make sure GuestProfile type is correctly defined and exported, e.g., in GuestProfiles.tsx or a shared types file:
/*
export interface OtherGuest { guestId: string; name: string; }
export interface Charge { chargeId: string; description: string; amount: number; date: string; }
export interface RoomInfo { roomId: string; roomNumber: string; floor: string | number; type: string; }
export interface RequestHistoryItem { requestId: string; requestType: string; submittedAt: string; status: string; }
export interface GuestProfile {
  guestId: string; name: string; checkInDate: string; checkOutDate?: string;
  status: "CheckedIn" | "CheckedOut" | "FutureBooking";
  otherGuests: OtherGuest[]; recentCharges: Charge[]; totalBill: number;
  preferences: string[]; room: RoomInfo; requestHistory: RequestHistoryItem[];
  email?: string; phone?: string;
}
*/
